import React, { useRef, useState } from "react";
import { message } from "antd";
import AttachmentButton from "./AttachmentButton";
import AttachmentsListModal from "./AttachmentsListModal";
import axios from "axios";
import { baseURL } from "../../../../globalUtils/axiosConfig";
import shippingIcon from '../../../../assets/shipping_icon.png';

const AttachmentContainer = ({ 
  recordType,
  record,
  color,
  additionalData,
  visibility,
  useShippingIcon = false,
  onAttachmentCountChange,
  onLocalAttachmentsChange,
  onUpdate,
  canAdd = true,
}) => {
  const [delAttachKey, setDelAttachKey] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [localAttachments, setLocalAttachments] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const inputFile = useRef(null);
  const borderColor = color;

  const humanizeRecordType = () => {
    return recordType
      .split('_')
      .map(word => word.toUpperCase())
      .join(' ');
  };

  let modalTitle;
  if (recordType === 'material_control_register') {
    modalTitle = `ATTACHMENTS`;
  } else {
    modalTitle = `${humanizeRecordType()} ATTACHMENTS`;
  }

  const createUrl = () => {
    return `${baseURL}/attachments`;
  }

  const deleteUrl = (recordId) => {
    return `${baseURL}/attachments/${recordId}`;
  }

  // Update local attachments when props change
  React.useEffect(() => {
    if (record?.attachments) {
      
      // Filter attachments based on additionalData
      let filteredAttachments = record.attachments;
      
      if (additionalData === 'signedDeliveryNote') {
        // Show only attachments with additionalData.signedDeliveryNote: true
        filteredAttachments = record.attachments.filter(attachment => 
          attachment.additionalData?.signedDeliveryNote === true
        );
      }
      // If no additionalData filter is specified, show all attachments
      // This allows the backend to control what attachments are returned
      
      setLocalAttachments(filteredAttachments);
    } else {
      setLocalAttachments([]);
    }
  }, [record, additionalData]);

  // Notify parent component when attachment count changes (only for filtered attachments)
  React.useEffect(() => {
    if (onAttachmentCountChange) {
      if (additionalData === "signedDeliveryNote") {
        // Only count filtered signed delivery note attachments
        onAttachmentCountChange(localAttachments.length);
      }
    }
  }, [localAttachments.length, onAttachmentCountChange, additionalData]);

  const handleShowImage = async (image) => {
    if (image.isLocal) {
      let url = URL.createObjectURL(image);
      window.open(url, "_blank");
    } else {
      try {
        const hideLoading = message.loading("Opening attachment...", 0);

        const response = await axios({
          url: image.path,
          method: 'GET',
          responseType: 'blob',
        });

        const mimeType = response.headers['content-type'] || 'application/octet-stream';

        const blob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;

        if (mimeType.startsWith('image/') || mimeType === 'application/pdf') {
          a.target = '_blank';  
        } else {
          a.download = image.name || `attachment_${Date.now()}`; 
        }

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setTimeout(() => window.URL.revokeObjectURL(url), 60000);

        hideLoading();
      } catch (error) {
        console.error('Error fetching attachment:', error);
        message.error(`Failed to load attachment: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const uploadFile = async (file) => {
    const attachableId = record?.id;

    // Handle signed delivery note files locally (no API call)
    if (additionalData === 'signedDeliveryNote') {
      setUploading(true);
      
      try {
        // Create local attachment object with file data
        const newAttachment = {
          id: Date.now(),
          key: Date.now(),
          name: file.name,
          filename: file.name,
          originalName: file.name,
          size: file.size,
          mimetype: file.type,
          uploadedAt: new Date().toISOString(),
          isLocal: true,
          file: file, // Store the actual file object
          additionalData: { signedDeliveryNote: true }
        };
        
        const updatedAttachments = [...localAttachments, newAttachment];
        setLocalAttachments(updatedAttachments);
        
        // Return attachments to parent component
        if (onLocalAttachmentsChange) {
          onLocalAttachmentsChange(updatedAttachments);
        }
        
        message.success('Signed delivery note attachment added Successfully');
        
      } catch (error) {
        console.error('Local storage error:', error);
        message.error('Failed to add attachment locally');
      } finally {
        setUploading(false);
      }
      return;
    }

    // Check for attachableId for regular API uploads
    if (!attachableId) {
      message.error(`ID not found for ${humanizeRecordType()}`);
      return;
    }

    // Regular API upload flow for other attachment types
    const formData = new FormData();
    let attachableType;
    if (recordType === 'material_control_register') {
      attachableType = 'MaterialControlRegister';
    } else if (recordType === 'delivery_note') {
      attachableType = 'DeliveryNote';
    } else {
      attachableType = 'ProgressStep';
    }

    formData.append('attachableType', attachableType);
    formData.append('attachableId', attachableId);
    formData.append('documents', file);
    
    try {
      setUploading(true);

      const response = await axios({
        method: 'POST',
        url: createUrl(),
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      message.success('Attachment uploaded successfully');

      // Update attachments with response or add to local state
      if (response.data && response.data.attachments) {
        setLocalAttachments(response.data.attachments);
      } else {
        // Fallback: add the uploaded file to local state with mock data
        const newAttachment = {
          id: Date.now(),
          key: Date.now(),
          name: file.name,
          filename: file.name,
          size: file.size,
          uploadedAt: new Date().toISOString(),
          isLocal: false
        };
        const updatedAttachments = [...localAttachments, newAttachment];
        setLocalAttachments(updatedAttachments);
      }

      // Notify parent to refresh data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Upload error:', error);

      if (error.response) {
        const status = error.response.status;
        const message_text = error.response.data?.message || 'Upload failed';

        if (status === 404) {
          message.error('Progress step not found. Please check the configuration.');
        } else if (status === 413) {
          message.error('File too large. Please select a smaller file.');
        } else if (status >= 500) {
          message.error('Server error. Please try again later.');
        } else {
          message.error(`Upload failed: ${message_text}`);
        }
      } else if (error.request) {
        message.error('Network error. Please check your connection and server status.');
      } else {
        message.error('Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const changeHandler = async (event) => {
    if (event.target.files.length < 1) {
      return;
    }

    // Upload each file immediately
    for (let i = 0; i < event.target.files.length; i++) {
      const file = event.target.files[i];
      await uploadFile(file);
    }

    // Clear the input
    event.target.value = null;
  };

  const handleDeleteImage = async (record) => {
    if (record?.isLocal) {
      // For local files (signed delivery notes), just filter them out
      const filteredImages = localAttachments.filter(
        (image) => image.key !== record.key
      );
      setLocalAttachments(filteredImages);
      
      // Notify parent component for signed delivery note attachments
      if (additionalData === 'signedDeliveryNote' && onLocalAttachmentsChange) {
        onLocalAttachmentsChange(filteredImages);
      }
      
      message.success('Local attachment removed successfully');
    } else {
      setDelAttachKey([...delAttachKey, record.key || record.id]);

      try {
        await axios.delete(deleteUrl(record.key || record.id));

        message.success('Attachment deleted successfully');

        // Remove from local state
        const filteredImages = localAttachments.filter(
          (image) => (image.key || image.id) !== (record.key || record.id)
        );
        setLocalAttachments(filteredImages);

        // Notify parent to refresh data
        if (onUpdate) {
          onUpdate();
        }
      } catch (error) {
        console.error('Delete error:', error);
        message.error('Failed to delete attachment');
      } finally {
        setDelAttachKey(delAttachKey.filter(key => key !== (record.key || record.id)));
      }
    }
  };

  return (
    <div className="progress-step-attachments">
      <div className="edit-icon-div" style={{ marginTop: '8px', marginBottom: '10px' }}>
        {(!visibility && canAdd) && (
          <>
            <input
              disabled={uploading}
              type="file"
              name="attachment"
              id={`attachment-${recordType}`}
              accept="doc/docx/xls/xlsx/ppt/pptx/pdf/jpg/jpeg/gif/png/mp4"
              style={{ display: "none" }}
              onChange={changeHandler}
              multiple
              ref={inputFile}
            />
            <label htmlFor={`attachment-${recordType}`}>
              <AttachmentButton 
                onClick={() => setModalVisible(true)} 
                loading={uploading} 
                style={{color, borderColor  }} 
                count={localAttachments.length} 
                disabled={!record && additionalData !== 'signedDeliveryNote'}
                visibility={visibility}
                useShippingIcon={useShippingIcon}
              />
            </label>
          </>
        )}
        {(!visibility && !canAdd) && (
          <AttachmentButton
            onClick={() => setModalVisible(true)}
            loading={uploading}
            style={{ color, borderColor }}
            count={localAttachments.length}
            disabled={!record && additionalData !== 'signedDeliveryNote'}
            visibility={visibility}
            useShippingIcon={useShippingIcon}
          />
        )}
        {visibility && (
          <AttachmentButton 
            onClick={() => setModalVisible(true)} 
            loading={uploading} 
            style={{color, borderColor  }} 
            count={localAttachments.length} 
            disabled={!record && additionalData !== 'signedDeliveryNote'}
            visibility={visibility}
            useShippingIcon={useShippingIcon}
          />
        )}
      </div>

      <AttachmentsListModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        attachments={localAttachments}
        onShowImage={handleShowImage}
        onDeleteImage={handleDeleteImage}
        delAttachKey={delAttachKey}
        title={modalTitle}
        onUpload={changeHandler}
        uploading={uploading}
        useShippingIcon={visibility || useShippingIcon}
        canAdd={canAdd}
      />
    </div>
  );
};

export default AttachmentContainer;
